import { CircularProgress, Fade, Menu, MenuItem, Modal } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { IoSearchSharp } from 'react-icons/io5';
import HeaderTextLayout from '../../../components/shared/HeaderTextLayout';
import { useEffect, useMemo, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { exportMenu } from '../../../constants/variables/arrays';
import { Refresh } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import {
  useImportProspectDriverMutation,
  useLazyGetProspectDriversQuery,
  useSendEmailMutation
} from '../../../api/prospectDriver';
import InfiniteScroll from 'react-infinite-scroll-component';
import Table from '../../../components/shared/Table';
import { useSelector } from 'react-redux';
import { actions } from '../../../redux/store/store';
import { checkForError } from '../../../constants/extras/errorHandlers';
import { sendSuccessMessage } from '../../../constants/extras/sendSuccessMsg';
import ConfirmDialog from '../../../components/shared/customModal/confirmDialog';
import moment from 'moment';
import '../../../components/shared/customModal/style.scss';
import { handleProspectStatusesBadge } from '../../../constants/extras/statusesBadge';

const pageLimit = 500;

export default function ProspectDrivers() {
  const [page, setPage] = useState(1);
  const [prospectDrivers, setProspectDrivers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [confirmation, setConfirmation] = useState({
    isOpen: false,
    data: null,
    fileName: ''
  });

  const [getDrivers, { isFetching }] = useLazyGetProspectDriversQuery();

  const fetchDrivers = async (param = {}) => {
    const { refetch, clearSearch } = param;

    const { data } = await getDrivers({
      page: refetch ? 1 : page,
      limit: pageLimit,
      search: clearSearch ? '' : searchQuery
    });

    if (refetch) setProspectDrivers(data?.data);
    else setProspectDrivers((prev) => [...prev, ...data?.data]);

    setTotal(data?.total);
    setHasMore(data?.data?.length === pageLimit);
  };

  useEffect(() => {
    fetchDrivers();
  }, [page]);

  const isRefetch = useSelector((state) => state.progress.isProcessComplete);
  const { jobId } = useSelector((state) => state.progress);

  useEffect(() => {
    if (!isRefetch) return;
    setSelectedDrivers([]);
    handleFilter();
    actions.progress.setIsProcessComplete(false);
  }, [isRefetch]);

  const handleFilter = (params = {}) => {
    const { clearSearch } = params;
    setProspectDrivers([]);
    setTotal(0);
    setHasMore(true);
    fetchDrivers({ refetch: true, clearSearch });
  };

  const [importDrivers, { isLoading }] = useImportProspectDriverMutation();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleImportDrivers = async (sendEmail) => {
    const { data: result, error } = await importDrivers({
      data: JSON.stringify(confirmation?.data),
      fileName: confirmation?.fileName,
      sendEmail
    });
    if (error || result?.error) {
      checkForError(result);
    } else {
      sendSuccessMessage(result?.message);
      if (result?.jobId) {
        actions.progress.setJobId(result?.jobId);
      } else {
        actions.progress.setIsProcessComplete(true);
      }
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();

      if (file.name.endsWith('.csv')) {
        reader.onload = (e) => {
          const text = e.target.result;

          const { data } = Papa.parse(text, {
            header: true,
            skipEmptyLines: true,
            trimHeaders: true
          });

          setConfirmation({ isOpen: true, data, fileName: file.name });
        };
        reader.readAsText(file);
      } else if (file.name.endsWith('.xlsx')) {
        reader.onload = (e) => {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          setConfirmation({
            isOpen: true,
            data: jsonData,
            fileName: file.name
          });
        };
        reader.readAsArrayBuffer(file);
      } else {
        console.error('Unsupported file type');
        return;
      }
    }
    setAnchorEl(null);
  };

  const handleConfirmAction = (action) => {
    handleImportDrivers(action);
    setConfirmation({ isOpen: false, data: null });
  };

  const [selectedDrivers, setSelectedDrivers] = useState([]);

  const handleSelect = (event, id) => {
    setSelectedDrivers((prev) => {
      if (event.target.checked) return [...prev, id];
      else return prev.filter((item) => item !== id);
    });
  };

  const handleSelectAll = (event) => {
    setSelectedDrivers(
      event.target.checked
        ? prospectDrivers
            ?.filter((e) => e?.status === 'not_sent')
            ?.map((e) => e._id)
        : []
    );
  };

  const [sendEmailConfirmation, setSendEmailConfirmation] = useState(false);
  const [sendEmail, { isLoading: EmailSendLoading }] = useSendEmailMutation();

  const handleSendEmail = async () => {
    const { data: result, error } = await sendEmail(selectedDrivers);
    if (error || result?.error) {
      checkForError(result);
    } else {
      sendSuccessMessage(result?.message);
      if (result?.jobId) {
        actions.progress.setJobId(result?.jobId);
      } else {
        actions.progress.setIsProcessComplete(true);
      }
    }
    handleOnClose();
  };

  const handleOnClose = () => {
    setSelectedDrivers([]);
    setSendEmailConfirmation(false);
  };

  const columns = useMemo(
    () => [
      ...(prospectDrivers?.filter((e) => e?.status === 'not_sent')?.length > 0
        ? [
            {
              name: 'checkbox',
              label: (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  <input
                    type="checkbox"
                    onClick={(event) => handleSelectAll(event)}
                    checked={
                      prospectDrivers?.filter((e) => e?.status === 'not_sent')
                        ?.length === selectedDrivers?.length
                    }
                    onChange={() => {}}
                    className="mainAdPayCheckbox"
                  />
                </div>
              ),
              className: 'table__td_has-checkbox',
              renderer: (item) => {
                if (item.status === 'not_sent') {
                  const isItemSelected =
                    selectedDrivers.indexOf(item._id) !== -1;
                  return (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        paddingTop: '0.18rem'
                      }}
                    >
                      <input
                        type="checkbox"
                        onClick={(event) => handleSelect(event, item._id)}
                        checked={isItemSelected}
                        onChange={() => {}}
                      />
                    </div>
                  );
                } else return <span></span>;
              }
            }
          ]
        : []),
      {
        label: 'Driver Name',
        name: 'firstName',
        sortable: true,
        renderer: (item) => `${item?.firstName} ${item?.lastName}`
      },
      {
        label: 'Email',
        name: 'email',
        sortable: true
      },
      {
        label: 'Phone Number',
        name: 'phone',
        sortable: true
      },
      {
        label: 'City',
        sortable: true,
        name: 'city'
      },
      {
        label: 'Imported From',
        sortable: true,
        name: 'imported_from'
      },
      {
        label: 'Import Date',
        sortable: true,
        renderer: ({ import_date }) =>
          moment(import_date).format('YYYY-MM-DD HH:mm A')
      },
      {
        label: 'Status',
        sortable: true,
        name: 'status',
        renderer: ({ status }) => {
          const formattedStatus = status
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

          const color = status === 'sent' ? '#E7FAE3' : '#F5CFD3';

          return (
            <div style={{ padding: '0.3rem' }}>
              <span style={{ ...handleProspectStatusesBadge(status), padding: '4px 8px' }}>
                {formattedStatus}
              </span>
            </div>
          );
        }
      },
      ...(prospectDrivers?.filter((e) => e?.status === 'not_sent')?.length > 0
        ? [
            {
              label: '',
              renderer: (item) => (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    height: '30px'
                  }}
                >
                  {item.status === 'not_sent' ? (
                    <button
                      style={{
                        backgroundColor: '#4195F5',
                        color: 'white',
                        padding: '0.5rem',
                        border: 'none',
                        borderRadius: '0.4rem',
                        fontSize: '0.75rem',
                        fontWeight: 400,
                        ...(isLoading || EmailSendLoading || jobId
                          ? { opacity: 0.5 }
                          : {})
                      }}
                      disabled={isLoading || EmailSendLoading || jobId}
                      onClick={() => {
                        setSelectedDrivers([item._id]);
                        setSendEmailConfirmation(true);
                      }}
                    >
                      Send Email
                    </button>
                  ) : (
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      -
                    </span>
                  )}
                </div>
              )
            }
          ]
        : [])
    ],
    [selectedDrivers, prospectDrivers, isLoading, EmailSendLoading, jobId]
  );

  return (
    <>
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div
              className="card"
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <HeaderTextLayout
                headerText="Prospect Drivers List"
                className="col-lg-2 col-md-2 main__driver"
                data={
                  prospectDrivers?.length > 0
                    ? `${prospectDrivers?.length} Drivers on Screen`
                    : ''
                }
                totalLength={total && `(${total} Drivers Found)`}
              >
                <div className="col-lg-10 col-md-10 col-sm-12 filter">
                  <div className="filter__inner p-0 justify-content-end">
                    {selectedDrivers?.length > 0 && (
                      <button
                        className="btn btn_accent"
                        disabled={isLoading || EmailSendLoading}
                        onClick={() => {
                          setSendEmailConfirmation(true);
                        }}
                      >
                        Send Email {`(${selectedDrivers?.length})`}
                      </button>
                    )}
                    <div className="filter__ctrls-holder filter__input-holder d-flex justify-content-end search__container">
                      <div
                        className="searchbar__container"
                        style={{ width: '250px' }}
                      >
                        <input
                          className="form__input customized__input"
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            if (e.target.value === '') {
                              setSearchQuery('');
                              handleFilter({ clearSearch: true });
                            }
                          }}
                          placeholder="Quick Search"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleFilter();
                            }
                          }}
                          disabled={isFetching}
                        />
                        {searchQuery?.length > 0 && (
                          <ClearIcon
                            className="clear__btn filter__search-icon"
                            onClick={() => {
                              if (!isFetching && searchQuery?.length > 0) {
                                setSearchQuery('');
                                handleFilter({ clearSearch: true });
                              }
                            }}
                            style={{
                              right: '45px',
                              top: '12px'
                            }}
                          />
                        )}
                        <button
                          disabled={searchQuery?.length <= 0}
                          className="search__button"
                          onClick={() => handleFilter()}
                        >
                          <IoSearchSharp />
                        </button>
                      </div>
                    </div>
                    <div>
                      <button
                        id="fade-button"
                        className="btn btn_block btn_accent btn_export_15"
                        onClick={(event) => setAnchorEl(event.currentTarget)}
                        variant="contained"
                        disabled={isLoading || EmailSendLoading}
                      >
                        {isLoading ? (
                          <div className="loading" style={{ color: 'white' }}>
                            <span
                              className="loading__text"
                              style={{ color: 'white' }}
                            >
                              Importing
                            </span>
                            ...
                          </div>
                        ) : (
                          <span>
                            Import <KeyboardArrowDownIcon className="mr-0" />
                          </span>
                        )}
                      </button>
                      <Menu
                        id="fade-menu"
                        MenuListProps={{
                          'aria-labelledby': 'fade-button'
                        }}
                        anchorEl={anchorEl}
                        open={open}
                        onClick={() => setAnchorEl(null)}
                        TransitionComponent={Fade}
                      >
                        {exportMenu.map(({ value, name }) => (
                          <MenuItem
                            key={name}
                            value={name}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            style={{ width: '100px' }}
                          >
                            <label htmlFor={name} style={{ fontWeight: 500 }}>
                              {name}
                            </label>
                            <input
                              type="file"
                              id={name}
                              style={{ display: 'none' }}
                              max={1}
                              onChange={handleFileChange}
                              accept={
                                name === 'CSV'
                                  ? '.csv'
                                  : name === 'XLSX'
                                  ? '.xlsx'
                                  : ''
                              }
                            />
                          </MenuItem>
                        ))}
                      </Menu>
                    </div>
                    <button
                      className="refresh_btn"
                      onClick={() => {
                        actions.progress.setIsProcessComplete(true);
                      }}
                    >
                      <Refresh className="refresh_icon" />
                    </button>
                  </div>
                </div>
              </HeaderTextLayout>

              <InfiniteScroll
                className="infinit_table"
                dataLength={prospectDrivers?.length}
                next={() => {
                  if (!isFetching && hasMore) {
                    setPage((prev) => prev + 1);
                  }
                }}
                hasMore={hasMore}
                style={{
                  paddingBottom:
                    prospectDrivers?.length === total ? '0px' : '10px'
                }}
                loader={
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{
                      height:
                        prospectDrivers && prospectDrivers?.length <= 0
                          ? '70vh'
                          : 'auto'
                    }}
                  >
                    <CircularProgress className="loading-progress" />
                  </div>
                }
                height={'80vh'}
                endMessage={
                  prospectDrivers &&
                  prospectDrivers?.length <= 0 && (
                    <div
                      style={{
                        height: '70vh'
                      }}
                      className="d-flex justify-content-center align-items-center"
                    >
                      <p>
                        <b>No Result Found</b>
                      </p>
                    </div>
                  )
                }
              >
                {isFetching && !prospectDrivers?.length ? (
                  <div className="loading-holder">
                    <CircularProgress className="loading-progress" />
                  </div>
                ) : (
                  <Table
                    columns={columns}
                    items={prospectDrivers}
                    className="table_drivers"
                    width={'100%'}
                  />
                )}
              </InfiniteScroll>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        title={'Confirm Action'}
        bodyText={`Do you want to send ${
          confirmation?.data?.length > 0
            ? `emails (total ${confirmation?.data?.length})`
            : 'email'
        } ?`}
        modalText="PAY"
        isOpen={sendEmailConfirmation}
        isLoading={EmailSendLoading}
        onClose={handleOnClose}
        confirmAction={handleSendEmail}
      />

      <CustomConfirmDialog
        title={'Confirm Action'}
        bodyText={`Do you want to send emails to all drivers (total ${confirmation?.data?.length}) ?`}
        isOpen={confirmation?.isOpen}
        isLoading={isLoading}
        onCancel={() => {
          setConfirmation({ isOpen: false, data: null });
        }}
        confirmActionNo={() => {
          handleConfirmAction(false);
        }}
        confirmAction={() => {
          handleConfirmAction(true);
        }}
      />
    </>
  );
}

const CustomConfirmDialog = (props) => {
  const { onCancel, isLoading } = props || {};
  return (
    <Modal
      open={props.isOpen}
      onClose={onCancel}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <div className="modal modal_dialog" style={{ height: 'auto' }}>
        <div className="modal__header">
          <h2 className="modal__title">{props?.title || ''}</h2>
        </div>
        <div className="modal__body">{props?.bodyText}</div>
        <div className="modal__footer">
          <button
            className="btn btn_reject"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className="btn"
            onClick={props.confirmActionNo}
            disabled={isLoading}
          >
            No
          </button>
          <button
            className="btn btn_accent"
            onClick={props.confirmAction}
            disabled={false}
          >
            Yes
            {isLoading && <CircularProgress className="btn__progress" />}
          </button>
        </div>
      </div>
    </Modal>
  );
};
